import { multipart } from '@villus/multipart';
import { createClient } from "graphql-ws";
import { Client, defaultPlugins, definePlugin, handleSubscriptions, useClient } from "villus";
import { useImpersonateTenant } from '~/composables/useImpersonateTenant';

export default defineNuxtPlugin(nuxtApp => {
  const { public: { gatewayUrl } } = useRuntimeConfig();

  const token = useApiAccessToken();
  const { impersonatedTenantId } = useImpersonateTenant();

  const wsClient = createClient({
    url: `${gatewayUrl.replace('https', 'wss')}/graphql/ws`,
    connectionParams() {
      return {
        authorization: `Bearer ${token.value?.accessToken}`,
        impersonateTenantId: impersonatedTenantId.value ?? undefined
      };
    }
  });

  const subscriptionsHandler = handleSubscriptions(operation => {
    return {
      subscribe: obs => {
        const dispose = wsClient.subscribe(
          {
            query: operation.query,
            variables: operation.variables
          },
          {
            next: (data) => {
              obs.next({ data });
            },
            error: (err) => {
              console.error('ws error', err);
              obs.error(err);
            },
            complete: () => {
              obs.complete();
            }
          }
        );
        return {
          unsubscribe: () => {
            dispose();
          },
        };
      },
    };
  });

  const useGqlAuth = (token: Ref<{ accessToken: string } | undefined>, tenantId: Ref<string | null>) => {
    return definePlugin(({ opContext }) => {
      opContext.headers.Authorization = `Bearer ${token.value?.accessToken}`;
      if (tenantId.value) {
        opContext.headers['x-impersonate-tenant-id'] = tenantId.value;
      }
    });
  }

  const useUanuthorizedHandler = definePlugin(({ afterQuery }) => {
    afterQuery(async (_, { response }) => {
      if (response?.status === 403) {
        var { path } = useRoute();
        setRedirectPath(path);
        navigateTo('/forbidden');
      }
    });
  });

  let client: Client | undefined;

  nuxtApp.hook('vue:setup', () => {
    client = useClient({
      url: `${gatewayUrl}/graphql`,
      use: [useGqlAuth(token, impersonatedTenantId), subscriptionsHandler, useUanuthorizedHandler, multipart(), ...defaultPlugins()],
      cachePolicy: 'cache-and-network'
    });
  });

  return {
    provide: {
      vClient: () => client,
      wsClient: () => wsClient
    }
  }
});